import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {deleteGridItems, getGridItemStorageForId, updateGridItem} from '@Components/base-grid';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';
import {openShareCollectionModal} from '@Modals/share-collection-modal';
import useWindowSize from '@Hooks/useWindowSize';
import {useDropTarget} from '@Hooks/useDropTarget';
import type {UserMediaType} from '@Libraries/user-media-library';
import {DEFAULT_NEW_FOLDER_NAME, saveUserCollection} from '@Libraries/user-media-library';
import type {GridItemBaseStorage, GridItemProps} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE, GridItem} from '@Components/base-grid/components/grid-item';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {openCollectionItemMoreOptionsModal} from '@Modals/collection-item-more-options-modal';
import {isTouchDevice} from '@Utils/browser.util';
import {FolderCard} from '@Components/folder-card/folder-card';
import {FolderCardMoreOptions} from '@Components/folder-card/components/folder-card-more-options/folder-card-more-options';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useMoveSavedFolderToSortedPosition} from '@Panels/user-media-panel/user-media-panel-hooks';
import {Icon} from '../../../icon-v2';
import {useAppDispatch, useAppSelector} from '@/hooks';

const SMALL_SCREEN_WIDTH = 560;

export interface GridCollectionItemStorage extends GridItemBaseStorage {
  type: GRID_ITEM_TYPE.COLLECTION;
  collectionName: string;
  isShared: boolean;
  collectionId: string;
  multiMediaType: string;
  creatorName?: string;
}

interface GridCollectionItemProps extends GridItemProps {
  isShared: boolean;
  collectionName: string;
  multiMediaType: UserMediaType;
  creatorName?: string;
}

export interface GridCollectionItemBackEndData {
  i: string;
  n: string;
  s: number;
}

export const getGridCollectionItemStorageFromBackendData = (data: GridCollectionItemBackEndData, multiMediaType: string): GridCollectionItemStorage => {
  return {
    type: GRID_ITEM_TYPE.COLLECTION,
    id: data.i,
    collectionId: data.i,
    collectionName: data.n,
    isShared: data.s !== 0,
    multiMediaType,
  };
};

export function GridCollectionItem({onClicked = (): void => {}, ...props}: GridCollectionItemProps): ReactElement<any> {
  const [renameMode, setRenameMode] = useState(false);
  const [areOptionsOpen, setAreOptionsOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const elementRef = useRef<HTMLLIElement>(null);
  const isDragHoveringFolder = useDropTarget(props.id, elementRef);
  const collectionNameRef = useRef<string | null>(null);
  const gridItemData = useAppSelector((state) => {
    return getGridItemStorageForId(state, props.gridId ?? '', props.id) as GridCollectionItemStorage;
  });
  const dispatch = useAppDispatch();
  const moveSavedFolderToSortedPosition = useMoveSavedFolderToSortedPosition();
  const {windowWidth} = useWindowSize();

  useEffect(() => {
    if (isNewCollection()) {
      renameModeOn();
    }
  }, []);

  const updateCollectionName = (name: string): void => {
    dispatch(
      updateGridItem({
        gridId: props.gridId ?? '',
        itemId: props.id,
        changes: {
          collectionName: name,
        },
      })
    );
  };

  const onCardNameCommitted = (committedName: string): void => {
    renameModeOff();
    if (committedName) {
      updateCollectionName(committedName);
      void saveCollectionName(committedName);
      return;
    }
    void saveCollectionName();
  };

  const onRenamingCancelled = (): void => {
    renameModeOff();
    if (isNewCollection()) {
      onCollectionRemoved();
    } else {
      updateCollectionName(collectionNameRef.current ?? DEFAULT_NEW_FOLDER_NAME);
    }
  };

  const isNewCollection = (): boolean => {
    return !gridItemData?.collectionId;
  };

  const saveCollectionName = async (name?: string): Promise<void> => {
    let collectionName = name ?? gridItemData?.collectionName;
    if (gridItemData?.collectionId && collectionName === collectionNameRef.current) {
      collectionNameRef.current = null;
      return;
    }
    if (!collectionName && collectionNameRef.current && gridItemData?.collectionId) {
      updateCollectionName(collectionNameRef.current);
      return;
    }
    setShowLoading(true);
    if (!collectionName) {
      collectionName = collectionNameRef.current ?? DEFAULT_NEW_FOLDER_NAME;
      updateCollectionName(collectionName);
    }
    collectionNameRef.current = null;
    try {
      const collectionId = await saveUserCollection({
        name: collectionName,
        collectionType: props.multiMediaType,
        collectionId: gridItemData?.collectionId,
      });
      handleNewCollection(collectionId, collectionName);
    } catch (e) {
      await onError(e);
    } finally {
      setShowLoading(false);
    }
  };

  /** A new tile led the grid while it was named; saved, it takes the position the sort order gives its name. */
  const handleNewCollection = (collectionId: string, collectionName: string): void => {
    if (isNewCollection()) {
      dispatch(
        updateGridItem({
          gridId: props.gridId ?? '',
          itemId: props.id,
          changes: {
            id: collectionId,
            collectionId,
          },
        })
      );
      moveSavedFolderToSortedPosition({folderId: collectionId, folderName: collectionName});
      trackPosterBuilderGA4Events(GA4EventName.UPLOADS_MODAL_CREATE_FOLDER);
    }
  };

  const isSmallScreen = (): boolean => {
    return windowWidth < SMALL_SCREEN_WIDTH || isTouchDevice();
  };

  const confirmAndDeleteCollection = (): void => {
    openConfirmDeleteModal({
      text: window.i18next.t('pmwjs_perma_delete_folder_confirmation'),
      showBtnDeletingState: true,
      onDeleteConfirmation: async (): Promise<void> => {
        await deleteCollection();
      },
    });
  };

  const confirmAndRemoveSharedCollection = (): void => {
    openConfirmDeleteModal({
      text: window.i18next.t('pmwjs_confirmation_removing_share_folder'),
      onDeleteConfirmation: (): void => {
        void removeSharedCollection();
      },
    });
  };

  const openCollectionOptionsModal = (): void => {
    openCollectionItemMoreOptionsModal({
      collectionName: gridItemData?.collectionName,
      deleteCollection,
      shareCollection: (): void => {
        void shareCollection();
      },
      isSharedCollection: gridItemData?.isShared,
      removeSharedCollection: (): void => {
        void removeSharedCollection();
      },
      renameCollection: (name: string) => {
        collectionNameRef.current = gridItemData?.collectionName;
        void saveCollectionName(name);
        updateCollectionName(name);
      },
    });
  };

  const getCollectionOptionsDropdown = (): ReactElement<any> => {
    if (showLoading) {
      return <CircularProgressLoader size={LOADER_SIZE.SMALL} />;
    }
    if (isSmallScreen()) {
      return <Icon icon="icon-menu-dots" size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} type={IconType.GHOST} onClick={openCollectionOptionsModal} />;
    }
    return (
      <FolderCardMoreOptions
        isShared={props.isShared}
        onShare={(): void => {
          void shareCollection();
        }}
        onRename={renameModeOn}
        onDelete={confirmAndDeleteCollection}
        onRemoveShared={confirmAndRemoveSharedCollection}
        onOpenChange={setAreOptionsOpen}
      />
    );
  };

  const deleteCollection = async (): Promise<void> => {
    await window.PMW.writeLocal('collection/delete', {
      collectionId: gridItemData?.collectionId,
      collectionType: props.multiMediaType,
    });
    onCollectionRemoved();
  };

  const removeSharedCollection = async (): Promise<void> => {
    setShowLoading(true);
    try {
      await window.PMW.writeLocal('collection/removeSharedCollection', {
        id: gridItemData?.collectionId,
        collectionType: props.multiMediaType,
      });
      onCollectionRemoved();
    } catch (e) {
      await onError(e);
    } finally {
      setShowLoading(false);
    }
  };

  const onCollectionRemoved = (): void => {
    if (!props.gridId || !props.gridSelectionGroupId) {
      return;
    }
    dispatch(
      deleteGridItems({
        gridId: props.gridId,
        gridSelectionGroupId: props.gridSelectionGroupId,
        itemIdsToDelete: [props.id],
      })
    );
  };

  const onCollectionClicked = (e: React.MouseEvent, isDoubleClick: boolean): void => {
    const isMoreOptionsClick = e.target instanceof Element && e.target.closest('.options-container') !== null;
    if (isMoreOptionsClick || !gridItemData?.collectionId || renameMode) {
      return;
    }
    onClicked(e, isDoubleClick);
  };

  const renameModeOn = (): void => {
    if (!collectionNameRef.current) {
      collectionNameRef.current = gridItemData?.collectionName;
    }
    setAreOptionsOpen(false);
    setRenameMode(true);
  };

  const renameModeOff = (): void => {
    setRenameMode(false);
  };

  const shareCollection = async (): Promise<void> => {
    if (!gridItemData?.collectionId) {
      console.warn('tried to share collection without ID');
      return;
    }
    openShareCollectionModal({
      collectionId: gridItemData?.collectionId,
      collectionType: props.multiMediaType,
    });
  };

  const onError = async (e: any, message = window.i18next.t('pmwjs_connectivity_error')): Promise<void> => {
    if (e) {
      console.error(e);
    }
    window.PMW.openErrorModal({
      message,
    });
  };

  const getTitle = (): string => {
    if (props.isShared) {
      return window.i18next.t('pmwjs_shared_collection_tooltip', {
        collectionName: gridItemData?.collectionName,
        creatorName: props.creatorName,
      });
    }
    return gridItemData?.collectionName ?? '';
  };

  return (
    <GridItem {...props} isSmallItem onClicked={onCollectionClicked} title={getTitle()} ref={elementRef}>
      <FolderCard
        name={gridItemData?.collectionName ?? ''}
        isShared={props.isShared}
        isDropTarget={isDragHoveringFolder}
        isEditing={renameMode}
        onNameCommit={onCardNameCommitted}
        onEditCancel={onRenamingCancelled}
        moreOptionsSlot={<div className="flex-center options-container">{getCollectionOptionsDropdown()}</div>}
        shouldAlwaysShowMoreOptions={isSmallScreen() || areOptionsOpen || showLoading}
      />
    </GridItem>
  );
}
